import React, { type ReactNode, useLayoutEffect, useRef } from 'react';

import styled from 'styled-components';

import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { withLocalePrefix } from 'shared/utils/translation';
import { UserMessage } from './user_message';
import { AssistantMessage } from './assistant_message';
import { Shimmer } from './shimmer';

const t = withLocalePrefix('shared.agent_chat');

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

type Labels = {
  loadEarlier: string;
  loadingEarlier: string;
  thinking: string;
};

type Props = {
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
  hasMoreMessages?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  children?: ReactNode;
  labels?: Partial<Labels>;
  scrollToBottomKey?: unknown;
};

export function MessageList({
  messages,
  streamingContent,
  isStreaming,
  hasMoreMessages,
  loadingEarlier,
  onLoadEarlier,
  children,
  labels,
  scrollToBottomKey,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevFirstIdRef = useRef<ChatMessage['id'] | null>(null);
  const prevLastIdRef = useRef<ChatMessage['id'] | null>(null);
  const prevScrollToBottomKeyRef = useRef<unknown>(undefined);
  const prevScrollHeightRef = useRef(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const firstId = messages.length ? messages[0].id : null;
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    // Prepend (Load earlier): the tail is unchanged but the head grew. Keep the user's view
    // anchored by pushing scrollTop down by exactly the height added above, so the message they
    // were reading stays put instead of jumping.
    const isPrepend =
      lastId === prevLastIdRef.current && firstId !== prevFirstIdRef.current;
    const scrollToBottomKeyChanged =
      scrollToBottomKey !== prevScrollToBottomKeyRef.current;

    if (isPrepend) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
    } else if (
      isStreaming ||
      lastId !== prevLastIdRef.current ||
      scrollToBottomKeyChanged
    ) {
      // Append or streaming: follow the latest message to the bottom.
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    prevScrollHeightRef.current = el.scrollHeight;
    prevFirstIdRef.current = firstId;
    prevLastIdRef.current = lastId;
    prevScrollToBottomKeyRef.current = scrollToBottomKey;
  }, [messages, streamingContent, isStreaming, scrollToBottomKey]);

  return (
    <Container ref={containerRef}>
      {hasMoreMessages && (
        <LoadEarlierRow>
          <TertiaryButton
            type="button"
            onClick={onLoadEarlier}
            disabled={loadingEarlier || isStreaming}
            size="sm"
          >
            {loadingEarlier
              ? labels?.loadingEarlier ?? t('loading_earlier')
              : labels?.loadEarlier ?? t('load_earlier')}
          </TertiaryButton>
        </LoadEarlierRow>
      )}

      {messages.map((message) =>
        message.role === 'user' ? (
          <UserMessage key={message.id} content={message.content} />
        ) : (
          <AssistantMessage
            key={message.id}
            content={message.content}
            animate={false}
          />
        )
      )}

      {isStreaming && streamingContent && (
        <AssistantMessage content={streamingContent} />
      )}

      {isStreaming && !streamingContent && (
        <Shimmer>{labels?.thinking ?? t('thinking')}</Shimmer>
      )}

      {children}

      <div ref={bottomRef} />
    </Container>
  );
}

const LoadEarlierRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Tokens.spacing['1']};
`;

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Tokens.spacing['3']};
  padding-top: ${Tokens.spacing['1.5']};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing['3']};
`;
